import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions';
import { useData } from '../hooks/useData.js';
import { PageHeader, LoadingSpinner, Button, ConfirmModal } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { apiFetch } from '../api/index.js';
import { useNotification } from '../context/NotificationContext.jsx';


const TableSchemaView = ({ table }) => {
    return (
        <div className="mt-2 pl-6">
            <div className="overflow-x-auto">
                <table className="w-full text-xs bg-gray-50 dark:bg-gray-800">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 text-left font-semibold">Column</th>
                            <th className="p-2 text-left font-semibold">Type</th>
                            <th className="p-2 text-left font-semibold">Nullable</th>
                            <th className="p-2 text-left font-semibold">Key</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {table.columns.map(col => (
                            <tr key={col.name}>
                                <td className="p-2 font-mono">{col.name}</td>
                                <td className="p-2 font-mono">{col.type}</td>
                                <td className="p-2">{col.is_nullable}</td>
                                <td className="p-2">{col.key}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const DatabaseExplorerPage = ({ token, currentUser }) => {
    const { data: databases, isLoading, error, refresh } = useData('/db-explorer/provisioned-databases', token);
    const [expandedDb, setExpandedDb] = useState(null);
    const [expandedTable, setExpandedTable] = useState(null);
    const [confirmDrop, setConfirmDrop] = useState(null);
    const { addNotification } = useNotification();
    const isSuperAdmin = hasPermission(currentUser, 'data_explorer:drop');

    useEffect(() => {
        const handleDataChange = () => refresh();
        window.addEventListener('datachanged:provisioned-databases', handleDataChange);
        return () => window.removeEventListener('datachanged:provisioned-databases', handleDataChange);
    }, [refresh]);

    const handleDropDatabase = (db) => {
        setConfirmDrop(db);
    };

    const confirmDeletion = async () => {
        if (!confirmDrop) return;
        try {
            await apiFetch(`/db-explorer/provisioned-databases/${confirmDrop.db_name}`, token, { method: 'DELETE' });
            addNotification(`Database "${confirmDrop.db_name}" and its user have been dropped.`);
            refresh();
        } catch (err) {
            addNotification(`Error dropping database: ${err.message}`, 'error');
        } finally {
            setConfirmDrop(null);
        }
    };

    const toggleDb = (dbName) => {
        setExpandedDb(prev => (prev === dbName ? null : dbName));
        setExpandedTable(null);
    };

    const toggleTable = (tableName) => {
        setExpandedTable(prev => (prev === tableName ? null : tableName));
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500 p-4 bg-red-50 rounded-md">Error: {error.message}</div>;

    return (
        <div>
            <PageHeader title="Data Explorer">
                <Button onClick={refresh} variant="secondary">
                    <DynamicIcon name="RefreshCw" className="mr-2 h-4 w-4" />
                    Rescan Databases
                </Button>
            </PageHeader>
            <p className="mb-6 text-gray-600 dark:text-gray-400">
                This page shows all tenant-specific databases that have been automatically provisioned by the platform.
            </p>

            <div className="space-y-4">
                {databases.map(db => (
                    <div key={db.db_name} className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                        <div className="p-4 flex justify-between items-center text-left">
                            <button onClick={() => toggleDb(db.db_name)} className="flex items-center gap-4 flex-grow">
                                <DynamicIcon name="Database" className="w-8 h-8 text-primary" />
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{db.db_name}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Assigned to: <span className="font-semibold">{db.tenant_name || 'Unassigned'}</span>
                                    </p>
                                </div>
                            </button>
                            <div className="flex items-center gap-2">
                                {isSuperAdmin && (
                                    <Button onClick={() => handleDropDatabase(db)} variant="danger">
                                        <DynamicIcon name="Trash2" className="mr-2 h-4 w-4" />
                                        Drop
                                    </Button>
                                )}
                                {/* <DynamicIcon name={expandedDb === db.db_name ? 'ChevronUp' : 'ChevronDown'} className="w-5 h-5" /> */}
                            </div>
                        </div>
                        {expandedDb === db.db_name && (
                            <div className="p-4 border-t dark:border-gray-700">
                                <h4 className="font-semibold mb-2">Tables ({db.tables.length})</h4>
                                <ul className="space-y-1">
                                    {db.tables.map(table => (
                                        <li key={table.name}>
                                            <button onClick={() => toggleTable(table.name)} className="w-full p-2 text-left rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center">
                                                <span>{table.name}</span>
                                                 <DynamicIcon name={expandedTable === table.name ? 'ChevronUp' : 'ChevronDown'} className="w-4 h-4" />
                                            </button>
                                            {expandedTable === table.name && <TableSchemaView table={table} />}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <ConfirmModal
                isOpen={!!confirmDrop}
                onClose={() => setConfirmDrop(null)}
                onConfirm={confirmDeletion}
                title={`Drop Database ${confirmDrop?.db_name}?`}
            >
                Are you absolutely sure you want to permanently delete this database and its associated user? This action cannot be undone.
            </ConfirmModal>
        </div>
    );
};

export default DatabaseExplorerPage;